import { useEffect, useMemo, useState, useCallback } from "react";
import { REPORT_DEFINITIONS } from "./lookups";
import { useAuth } from "../../context/AuthContext";
import "./reports.css";

function TableSpinner() {
  return (
    <div className="cdlis-table-loading">
      <div className="cdlis-spinner"></div>
      <p>Syncing with database...</p>
    </div>
  );
}

export default function Reports1() {
  const [selectedReportId, setSelectedReportId] = useState("month_tran");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportFiles, setReportFiles] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // --- Deletion State ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { token } = useAuth();

  // Layout: File Name, Report Type, Status, Requested On, Actions
  const gridLayout = '2.5fr 1.5fr 1fr 1.2fr 1.2fr';

  // Determine if the selected report belongs to a User or Account group
  const isUserOrAccountReport = useMemo(() => {
    const report = REPORT_DEFINITIONS.find(r => r.id === selectedReportId);
    if (!report) return false;
    if (report.id === 'disabled_acct_range') return false;
    const groupName = report.group.toLowerCase();
    return groupName.includes("user") || groupName.includes("account");
  }, [selectedReportId]);

  const fetchReports = useCallback(async (isSilent = false) => {
    
    try {
      if (!isSilent) setLoading(true);
      const response = await fetch('/api/reports/list-reports?limit=20', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

      const result = await response.json();
      if (result.success) {
        setReportFiles(result.data);
      }
    } catch (err) {
      if (!isSilent) setError("Connection error: Could not reach the report service.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Polling logic for active reports
  useEffect(() => {
    const active = reportFiles.some(r => 
      ['PENDING', 'STARTED', 'WORKING', 'PROCESSING'].includes(r.status?.toUpperCase())
    );

    if (active) {
      const interval = setInterval(() => fetchReports(true), 5000);
      return () => clearInterval(interval);
    }
  }, [reportFiles, fetchReports]);

  const formatDateForDB = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${m}/${d}/${y}`;
  };

  const triggerDownload = (url, fileName) => {
    if (!url) {
        alert("The report is marked complete, but the download link is currently unavailable.");
        return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // --- Deletion Actions ---
  const openDeleteModal = (report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;
    try {
      setIsDeleting(true);
      // POST /api/reports/delete-report/:id
      const res = await fetch(`/api/reports/delete-report/${reportToDelete.requestID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (result.success) {
        setShowDeleteModal(false);
        setReportToDelete(null);
        fetchReports(true); 
      } else {
        alert(result.message || "Failed to delete report.");
      }
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const groupedReports = useMemo(() => {
    return REPORT_DEFINITIONS.reduce((acc, report) => {
      if (!acc[report.group]) acc[report.group] = [];
      acc[report.group].push(report);
      return acc;
    }, {});
  }, []);

  const getFriendlyReportName = (id) => {
    const match = REPORT_DEFINITIONS.find(r => r.id === id);
    return match ? match.name : id;
  };

  async function generateReport() {
    if (!isUserOrAccountReport && (!startDate || !endDate)) {
        setError("Please select both a Start and End Date.");
        return;
    }

    try {
      setGenerating(true);
      setError("");
      
      const res = await fetch('/api/reports/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            reportType: selectedReportId, 
            startDate: isUserOrAccountReport ? "01/01/2026" : formatDateForDB(startDate), 
            endDate: isUserOrAccountReport ? "01/01/2026" : formatDateForDB(endDate) 
        })
      });
      const result = await res.json();
      if (result.success) {
        setStartDate(""); 
        setEndDate(""); 
        fetchReports(true);
      } else {
        setError(result.error || "Failed to trigger report generation.");
      }
    } catch (e) { 
        setError(e.message); 
    } finally { 
        setGenerating(false); 
    }
  }

  return (
    <div className="cdlis-app">
      <header className="cdlis-header">
        <h1 className="cdlis-title">CDLIS Reports Dashboard</h1>
      </header>

      <main className="cdlis-body">
        <div className="cdlis-shell">
          
          {/* GENERATE SECTION */}
          <section className="cdlis-card-tight">
            <div className="cdlis-banner-search">Generate Report</div>
            <div className="cdlis-card-body">
              <div className="cdlis-form-grid">
                <div className="cdlis-field">
                  <label className="cdlis-label">Report Type</label>
                  <select 
                    className="cdlis-input" 
                    value={selectedReportId} 
                    onChange={e => { setSelectedReportId(e.target.value); setError(""); }}
                  >
                    {Object.entries(groupedReports).map(([group, reports]) => (
                      <optgroup key={group} label={group}>
                        {reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {!isUserOrAccountReport ? (
                  <>
                    <div className="cdlis-field">
                      <label className="cdlis-label">Start Date</label>
                      <input type="date" className="cdlis-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div className="cdlis-field">
                      <label className="cdlis-label">End Date</label>
                      <input type="date" className="cdlis-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                  </>
                ) : (
                  <div className="cdlis-field" style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', paddingTop: '20px' }}>
                    <p style={{ margin: 0, color: '#0056b3', fontSize: '0.95rem', fontWeight: '500' }}>
                      ℹ️ This report is generated based on the current run date for Account/Directory records.
                    </p>
                  </div>
                )}
              </div>

              <div className="cdlis-actions">
                <button className="cdlis-primary-btn" onClick={generateReport} disabled={generating}>
                  {generating ? "Requesting..." : "Generate Report"}
                </button>
              </div>
              {error && <div className="cdlis-error" style={{marginTop: '10px'}}>{error}</div>}
            </div>
          </section>

          {/* HISTORY SECTION */}
          <section className="cdlis-card-tight">
            <div className="cdlis-banner-results" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Generated Report History</span>
              <button onClick={() => fetchReports()} className="cdlis-refresh-btn">Sync 🔄</button>
            </div>
            
            <div className="cdlis-table-scroll" style={{ position: 'relative', minHeight: '250px' }}>
              {loading && <TableSpinner />}
              
              <div className="cdlis-table-header" style={{ gridTemplateColumns: gridLayout }}>
                <div>File Name</div>
                <div>Report Type</div>
                <div>Status</div>
                <div>Requested On</div>
                <div style={{ textAlign: 'right' }}>Action</div>
              </div>
              
              {!loading && reportFiles.length > 0 ? (
                reportFiles.map((report, idx) => {
                  const statusUpper = report.status?.toUpperCase();
                  const isComplete = statusUpper === 'COMPLETE' || statusUpper === 'COMPLETED';
                  const isWorking = ['STARTED', 'WORKING', 'PENDING', 'PROCESSING'].includes(statusUpper);

                  return (
                    <div key={idx} className="cdlis-table-row" style={{ gridTemplateColumns: gridLayout }}>
                      <div className="cdlis-file-name" title={report.fileName}>
                        {isComplete ? '📗 ' : '⏳ '} {report.fileName}
                      </div>
                      <div className="cdlis-report-type" style={{ fontSize: '0.9rem', color: '#555' }}>
                        {getFriendlyReportName(report.reportType)}
                      </div>
                      <div>
                        <span className={`status-badge ${report.status?.toLowerCase()}`}>
                            {report.status}
                        </span>
                      </div>
                      <div>
                        {report.requestDate ? 
                          new Date(report.requestDate).toLocaleString('en-US', {
                            year: 'numeric', month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
                          }) : '—'}
                      </div>
                      <div className="cdlis-action-cell">
                        {isComplete ? (
                          <button onClick={() => triggerDownload(report.downloadUrl, report.fileName)} className="cdlis-file-btn">
                            Download
                          </button>
                        ) : (
                          <button className="cdlis-file-btn disabled" disabled>
                            {isWorking ? 'Working' : (report.status === 'FAILED' ? 'Failed' : 'Waiting')}
                          </button>
                        )}
                        <button 
                            className="cdlis-delete-btn" 
                            onClick={() => openDeleteModal(report)}
                            title="Delete Report"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : !loading && (
                <div className="cdlis-empty">No reports found in history.</div>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* --- Delete Confirmation Modal --- */}
      {showDeleteModal && (
        <div className="cdlis-modal-overlay">
          <div className="cdlis-modal">
            <div className="cdlis-modal-header">
              <h3>Confirm Deletion</h3>
              <button className="cdlis-modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="cdlis-modal-body">
              <p>Are you sure you want to delete this report? This action cannot be undone.</p>
            </div>
            <div className="cdlis-modal-footer">
              <button className="cdlis-cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button 
                className="cdlis-danger-btn" 
                onClick={confirmDelete} 
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}